import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, FileText, DollarSign, TrendingUp, Clock, Building2, MapPin, Briefcase, Send, BarChart3, Eye } from "lucide-react";
import EntityLink, { Breadcrumbs } from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import { Entity360Shell, MetricCard, TimelineCard, ReputationDetailCard } from "../components/shared/OSComponents";
import { LenderServicingPanel } from "../components/shared/ServicingPanel";
import { fmtUSD } from "../utils/currency";

import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

export default function Lender360Page({ userId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("deals_reviewed");
  const [reputation, setReputation] = useState(null);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  useEffect(() => {
    axios.get(`${API}/entity/lender/${userId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    axios.get(`${API}/reputation/lender/${userId}`, { headers }).then(r => setReputation(r.data)).catch(() => {});
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Lender not found</div>;

  const { profile, stats, loan_boxes, deals_reviewed, term_sheets, funded_deals, outreach, servicing } = data;
  const crumbs = [...breadcrumbs, { type: "lender", id: userId, label: profile.full_name || "Lender" }];
  const criteria = profile.lending_criteria || {};

  const relatedEntities = [
    ...deals_reviewed.slice(0, 5).map(d => ({ type: "deal", id: d.lead_id, title: `${LOAN_LABELS[d.loan_type] || d.loan_type} — ${d.state || ""}`, subtitle: d.property_address || "" })),
    ...funded_deals.slice(0, 3).map(d => ({ type: "deal", id: d.lead_id, title: `Funded: ${LOAN_LABELS[d.loan_type] || d.loan_type}`, subtitle: d.state || "" })),
  ];
  const quickActions = [
    { id: "view_deals", label: "View Reviewed Deals", icon: Building2, onClick: () => setTab("deals_reviewed") },
    { id: "view_ts", label: "View Term Sheets", icon: FileText, onClick: () => setTab("term_sheets") },
    ...(isInternal ? [{ id: "send_outreach", label: "Send Deal Outreach", icon: Send, onClick: () => {} }] : []),
    ...(isInternal ? [{ id: "generate_report", label: "Generate Report", icon: BarChart3, onClick: () => {} }] : []),
    ...(isInternal ? [{ id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") }] : []),
  ];
  const tabs = [
    { key: "deals_reviewed", label: `Deals (${stats.deals_reviewed})` },
    { key: "term_sheets", label: `Term Sheets (${stats.term_sheets_submitted})` },
    { key: "funded", label: `Funded (${stats.deals_funded})` },
    { key: "servicing", label: "Servicing" },
    ...(loan_boxes.length ? [{ key: "loan_boxes", label: `Loan Boxes (${loan_boxes.length})` }] : []),
    ...(isInternal && outreach.length ? [{ key: "outreach", label: "Outreach" }] : []),
    { key: "reputation", label: "Reputation" },
    ...(isInternal ? [{ key: "comms", label: "Communications" }] : []),
    ...(isInternal ? [{ key: "notes", label: "Notes" }] : []),
  ];
  const kpis = [
    { label: "Deals Reviewed", value: stats.deals_reviewed, icon: Building2 },
    { label: "Term Sheets", value: stats.term_sheets_submitted, icon: FileText },
    { label: "Deals Funded", value: stats.deals_funded, icon: TrendingUp, accent: true },
    { label: "Total Funded", value: fmtUSD(stats.total_funded_amount), icon: DollarSign },
    { label: "Avg Response", value: stats.avg_response_days !== null ? `${stats.avg_response_days}d` : "—", icon: Clock },
    { label: "Loan Boxes", value: stats.loan_boxes, icon: Briefcase },
  ];

  return (
    <Entity360Shell type="lender" title={profile.full_name}
      subtitle={[profile.company, profile.company_type, profile.location, profile.email].filter(Boolean).join(" | ")}
      kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab} onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} reputation={reputation?.score} relatedEntities={relatedEntities}>

      {criteria && (criteria.loan_types || criteria.states) && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 mb-4">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Lending Criteria</h3>
          <div className="flex flex-wrap gap-2">
            {criteria.loan_types?.map(lt => <span key={lt} className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100">{lt}</span>)}
            {criteria.states?.map(st => <span key={st} className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#faf7f2] text-[#888] border border-[#e8e0d0]">{st}</span>)}
            {criteria.min_loan && <span className="text-[10px] text-[#888]">Min: {fmtUSD(criteria.min_loan)}</span>}
            {criteria.max_loan && <span className="text-[10px] text-[#888]">Max: {fmtUSD(criteria.max_loan)}</span>}
          </div>
        </div>
      )}

      {tab === "deals_reviewed" && (
        <div className="space-y-3">
          {deals_reviewed.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No deals reviewed.</div> : deals_reviewed.map(d => (
            <div key={d.lead_id} className="rounded-xl border border-[#e8e0d0] bg-white p-4 flex items-center justify-between hover:shadow-sm transition-shadow cursor-pointer" onClick={() => onNavigate("deal", d.lead_id)} data-testid={`lender-deal-${d.lead_id}`}>
              <div><div className="flex items-center gap-2"><span className="text-xs font-bold text-[#222]">{LOAN_LABELS[d.loan_type] || d.loan_type}</span><span className="text-[10px] text-[#888]">{d.state}</span></div><div className="text-[10px] text-[#999] mt-0.5">{d.property_address || d.lead_id.slice(0, 12)}</div></div>
              <div className="text-xs font-semibold text-[#333]">{d.purchase_price ? fmtUSD(d.purchase_price) : "—"}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "term_sheets" && (
        <div className="space-y-2">
          {term_sheets.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No term sheets.</div> : term_sheets.map(ts => (
            <div key={ts.ts_id} className="rounded-xl border border-[#e8e0d0] bg-white p-3 cursor-pointer hover:shadow-sm" onClick={() => onNavigate("deal", ts.lead_id)}>
              <div className="flex items-center justify-between"><div><span className="text-xs font-medium text-[#333]">TS #{ts.ts_id?.slice(0, 8)}</span>{ts.loan_amount && <span className="ml-2 text-xs font-bold text-emerald-600">{fmtUSD(ts.loan_amount)}</span>}</div><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${ts.status === "submitted" ? "bg-blue-100 text-blue-600" : ts.status === "accepted" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-600"}`}>{ts.status}</span></div>
              <div className="flex gap-3 text-[10px] text-[#999] mt-1">{ts.interest_rate && <span>{ts.interest_rate}% rate</span>}{ts.points && <span>{ts.points} pts</span>}{ts.term_months && <span>{ts.term_months}mo</span>}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "funded" && (
        <div className="space-y-3">
          {funded_deals.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No funded deals.</div> : funded_deals.map(d => (
            <div key={d.lead_id} className="rounded-xl border border-[#e8e0d0] bg-white p-4 cursor-pointer hover:shadow-sm" onClick={() => onNavigate("deal", d.lead_id)}>
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#222]">{LOAN_LABELS[d.loan_type] || d.loan_type}</span><span className="text-[10px] text-[#888]">{d.state}</span></div>
            </div>
          ))}
        </div>
      )}

      {tab === "loan_boxes" && (
        <div className="space-y-3">
          {loan_boxes.map((box, i) => (
            <div key={`box-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h4 className="text-xs font-bold text-[#222] mb-2">{box.name || `Box ${i + 1}`}</h4>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                {box.loan_types && <div><span className="text-[#999]">Types:</span> <span className="text-[#333]">{box.loan_types.join(', ')}</span></div>}
                {box.states && <div><span className="text-[#999]">States:</span> <span className="text-[#333]">{box.states.join(', ')}</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "outreach" && (
        <div className="space-y-2">
          {outreach.map((o, i) => (
            <div key={`out-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3"><p className="text-xs text-[#333]">{o.type || o.action || "Outreach"}</p><p className="text-[10px] text-[#999] mt-0.5">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</p></div>
          ))}
        </div>
      )}

      {tab === "reputation" && <ReputationDetailCard data={reputation} entityType="Lender" />}

      {tab === "servicing" && <LenderServicingPanel servicing={servicing} />}

      {tab === "comms" && <EntityComms entityType="lender" entityId={userId} />}
      {tab === "notes" && <EntityNotes entityType="lender" entityId={userId} />}
    </Entity360Shell>
  );
}
